import React from "react";

import { getLanguageFromLocalStorage, useTranslation } from "utils";

import { LANGUAGE_CODE } from "consts";

import { Checkbox, Flex, Form, FormInstance, Typography } from "components";

import { SignupTermsText } from "pages/app/account/components/SignupTermsText.tsx";

export interface SignupCheckboxValues {
  terms?: boolean;
  businessUseConfirmed?: boolean;
  germanJurisdictionAccepted?: boolean;
}

interface SignupCheckboxDefinition {
  name: keyof SignupCheckboxValues;
  dataTestId: string;
  isVisible: () => boolean;
}

// TODO: This should be extended based on business cases in future places... will
// refactor when it makes sense to do so
const isHungarian = () => getLanguageFromLocalStorage() === LANGUAGE_CODE.HUNGARIAN;

const SIGNUP_CHECKBOXES: SignupCheckboxDefinition[] = [
  { name: "terms", dataTestId: "sign-up-checkbox", isVisible: () => true },
  { name: "businessUseConfirmed", dataTestId: "sign-up-checkbox-business-use", isVisible: () => true },
  { name: "germanJurisdictionAccepted", dataTestId: "sign-up-checkbox-german-jurisdiction", isVisible: isHungarian },
];

const getVisibleSignupCheckboxes = () => SIGNUP_CHECKBOXES.filter(({ isVisible }) => isVisible());

export const useSignupCheckboxesAccepted = <Values extends SignupCheckboxValues>(form: FormInstance<Values>): boolean =>
  Form.useWatch((values: Values) => getVisibleSignupCheckboxes().every(({ name }) => values[name] === true), form) ?? false;

export const SignupCheckboxes = () => {
  const { t } = useTranslation("signinup");

  const labels: Record<keyof SignupCheckboxValues, React.ReactNode> = {
    terms: <SignupTermsText leadIn={t("terms.agree")} />,
    businessUseConfirmed: <Typography variant="paragraph-m">{t("consents.businessUse")}</Typography>,
    germanJurisdictionAccepted: <Typography variant="paragraph-m">{t("consents.germanJurisdiction")}</Typography>,
  };

  return (
    <Flex vertical gap={8}>
      {getVisibleSignupCheckboxes().map(({ name, dataTestId }) => (
        <Form.Item
          key={name}
          name={name}
          valuePropName="checked"
          rules={[
            {
              required: true,
              message: t("errors.checkboxRequired"),
              validator: (_, value) => (value ? Promise.resolve() : Promise.reject()),
            },
          ]}
        >
          <Checkbox data-testid={dataTestId} className="margin-0">
            {labels[name]}
          </Checkbox>
        </Form.Item>
      ))}
    </Flex>
  );
};
